import React from 'react';
import { Card, CardContent, Grid, Stack, Typography, Box } from '@mui/material';
import {
    Assessment as AssessmentIcon,
    AttachMoney as AttachMoneyIcon,
    FactCheck as FactCheckIcon,
    TrendingUp as TrendingUpIcon,
    WarningAmber as WarningAmberIcon,
    ReceiptLong as ReceiptLongIcon,
    LockClock as LockClockIcon,
} from '@mui/icons-material';
import { formatMoney, formatNumber, percentage, toNumber } from '../utils/formatters';

const KpiCard = ({ title, value, subtitle, icon, color = 'primary' }) => (
    <Card
        elevation={0}
        sx={{
            height: '100%',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
        }}
    >
        <CardContent>
            <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Box>
                    <Typography variant="body2" color="text.secondary">
                        {title}
                    </Typography>

                    <Typography variant="h5" fontWeight={900}>
                        {value}
                    </Typography>

                    {subtitle && (
                        <Typography variant="caption" color="text.secondary">
                            {subtitle}
                        </Typography>
                    )}
                </Box>

                <Box
                    sx={{
                        minWidth: 42,
                        height: 42,
                        borderRadius: 2,
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: `${color}.light`,
                        color: `${color}.contrastText`,
                    }}
                >
                    {icon}
                </Box>
            </Stack>
        </CardContent>
    </Card>
);

const OrderServiceKpiGrid = ({ summary = {} }) => {
    const totalOrders = toNumber(summary.total_orders);
    const totalAmount = toNumber(summary.total_amount);
    const totalEjecutadas = toNumber(summary.total_ejecutadas);
    const totalCicloCobro = toNumber(summary.total_en_ciclo_cobro);

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                    title="Total órdenes"
                    value={formatNumber(totalOrders)}
                    subtitle="Órdenes registradas"
                    icon={<AssessmentIcon />}
                />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                    title="Monto acumulado"
                    value={formatMoney(totalAmount)}
                    subtitle="Valor total del periodo"
                    icon={<AttachMoneyIcon />}
                    color="success"
                />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                    title="Ejecución"
                    value={`${percentage(totalEjecutadas, totalOrders)}%`}
                    subtitle={`${formatNumber(totalEjecutadas)} finalizadas o posteriores`}
                    icon={<TrendingUpIcon />}
                    color="warning"
                />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                    title="Ciclo de cobro"
                    value={`${percentage(totalCicloCobro, totalOrders)}%`}
                    subtitle={`${formatNumber(totalCicloCobro)} valorizadas/facturadas/cerradas`}
                    icon={<FactCheckIcon />}
                    color="info"
                />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
                <KpiCard
                    title="Pendiente valorizar"
                    value={formatNumber(summary.total_finalizadas_pendientes_valorizar)}
                    subtitle={formatMoney(summary.amount_pendiente_valorizar)}
                    icon={<WarningAmberIcon />}
                    color="warning"
                />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
                <KpiCard
                    title="Pendiente facturar"
                    value={formatNumber(summary.total_pendientes_facturar)}
                    subtitle={formatMoney(summary.amount_pendiente_facturar)}
                    icon={<ReceiptLongIcon />}
                    color="info"
                />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
                <KpiCard
                    title="Pendiente cerrar"
                    value={formatNumber(summary.total_pendientes_cerrar)}
                    subtitle={formatMoney(summary.amount_pendiente_cerrar)}
                    icon={<LockClockIcon />}
                    color="secondary"
                />
            </Grid>
        </Grid>
    );
};

export default OrderServiceKpiGrid;